import dayjs from "dayjs";
import { resumenDeLineas } from "../../../helpers/beautyLineas";

/* Piezas puras de la agenda de Turnos Beauty: formato, estados y armado de columnas/filas. */

export const DATE_FORMAT = "YYYY-MM-DD";
export const hoyISO = () => dayjs().format(DATE_FORMAT);
export const desplazarDia = (date, delta) => dayjs(date).add(delta, "day").format(DATE_FORMAT);

export const toMin = (hhmm) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm || "");
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

export const toHHmm = (min) => {
  const n = ((Math.round(min) % 1440) + 1440) % 1440;
  return `${String(Math.floor(n / 60)).padStart(2, "0")}:${String(n % 60).padStart(2, "0")}`;
};

export const plata = (n) => `$ ${Number(n || 0).toLocaleString("es-AR")}`;

export const duracionLabel = (min) => {
  const total = Number(min) || 0;
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (!h) return `${m} min`;
  return m ? `${h} h ${m} min` : `${h} h`;
};

export const STATUS_META = {
  confirmed: { label: "Confirmado", tone: "confirmed" },
  reserved: { label: "Reservado", tone: "reserved" },
  pending: { label: "Pendiente", tone: "pending" },
  no_show: { label: "No asistió", tone: "no-show" },
  cancelled: { label: "Cancelado", tone: "cancelled" },
  present: { label: "Presente", tone: "present" },
};
export const statusMeta = (status) => STATUS_META[status] || { label: status || "", tone: "holding" };

export const esTerminal = (status) => status === "cancelled" || status === "no_show";

/** Arrived: scanned, or the balance was charged at the PDV (the back stamps checkedInAt), same as Canchas. */
export const estaPresente = (t) => Boolean(t?.checkedInAt) && !esTerminal(t?.status);

/** The state the agenda shows: "present" wins over the reservation status once the person arrived. */
export const estadoVisible = (t) => (estaPresente(t) ? "present" : t?.status);

export const puedeConfirmar = (t) => (t.status === "pending" || t.status === "reserved") && !t.isPast;
// Someone who already arrived can no longer be cancelled or marked as a no-show (same rule as Canchas).
export const puedeCancelar = (t) => !esTerminal(t.status) && !estaPresente(t);
// A reserved/confirmed appointment that has not started yet and whose person has not arrived can be moved
// (the back rejects "pending" ones: the deposit is still unpaid).
export const puedeMover = (t) => ["reserved", "confirmed"].includes(t.status) && !t.isPast && !estaPresente(t);
// Manual check-in (same rule as Canchas): reserved/confirmed, not over and not already arrived.
export const puedeMarcarIngreso = (t) => ["reserved", "confirmed"].includes(t.status) && !t.isPast && !estaPresente(t);
export const puedeNoShow = (t) => !esTerminal(t.status) && !estaPresente(t);

export const nombreCliente = (t) => (t?.name || "").trim() || "Sin nombre";
/** "Corte (Largo) + Brushing, Tintura": the display rule of the variants and add-ons. */
export const serviciosLabel = (t) => resumenDeLineas(t?.services);

/**
 * Minutes the professional stays busy after the service (cleaning / preparing). It is a snapshot kept by
 * the back; cancelled and no-show appointments free it, and old ones carry no value (0).
 */
export const minutosPreparacion = (t) => {
  const n = Number(t?.bufferAfterMinutes);
  return n > 0 && !esTerminal(t?.status) ? n : 0;
};

/** Minute where the service ends (`slotEnd`/`endMin` are the service end, never the buffer end). */
export const finServicioMin = (t) => (Number.isInteger(t?.endMin) ? t.endMin : toMin(t?.slotEnd));

/** "HH:mm" until which the professional stays busy, or null when the appointment has no preparation. */
export const preparacionHasta = (t) => {
  const fin = finServicioMin(t);
  const n = minutosPreparacion(t);
  return n && fin != null ? toHHmm(fin + n) : null;
};

const turnoNormalizado = (t) => {
  const startMin = Number.isInteger(t.startMin) ? t.startMin : toMin(t.slotStart);
  let endMin = Number.isInteger(t.endMin) ? t.endMin : toMin(t.slotEnd);
  if (startMin == null || endMin == null) return null;
  if (endMin <= startMin) endMin += 1440;
  return { ...t, startMin, endMin };
};

const bloqueNormalizado = (b) => {
  if (b.allDay) return { ...b, startMin: null, endMin: null };
  const startMin = toMin(b.slotStart);
  let endMin = toMin(b.slotEnd);
  if (startMin == null || endMin == null) return null;
  if (endMin <= startMin) endMin += 1440;
  return { ...b, startMin, endMin };
};

/**
 * Arma el modelo del día: un eje continuo (minutos, puede pasar de 1440) y, por profesional,
 * sus tramos de trabajo, turnos y bloqueos. `nadieAtiende` = ningún tramo ni nada que mostrar.
 * Las filas tienen la granularidad del salón (mínimo 15 para que la celda sea legible).
 */
export function buildAgendaModel(professionals = [], stepMinutes = 15) {
  const step = Number(stepMinutes) > 0 ? Number(stepMinutes) : 15;
  const rowMinutes = step >= 15 ? step : 15;
  let lo = Infinity;
  let hi = -Infinity;
  const tocar = (s, e) => {
    lo = Math.min(lo, s);
    hi = Math.max(hi, e);
  };

  const columns = professionals.map((p) => {
    const intervals = (p.workIntervals || [])
      .map((w) => ({ startMin: Number(w.start), endMin: Number(w.end) }))
      .filter((w) => Number.isFinite(w.startMin) && w.endMin > w.startMin);
    const appointments = (p.appointments || []).map(turnoNormalizado).filter(Boolean);
    const blocks = (p.blocks || []).map(bloqueNormalizado).filter(Boolean);
    intervals.forEach((w) => tocar(w.startMin, w.endMin));
    appointments.forEach((a) => tocar(a.startMin, a.endMin + minutosPreparacion(a)));
    blocks.forEach((b) => b.startMin != null && tocar(b.startMin, b.endMin));
    return { id: String(p.id), name: p.name, active: p.active !== false, intervals, appointments, blocks };
  });

  const hayTramos = columns.some((c) => c.intervals.length > 0);
  if (!Number.isFinite(lo) || (!hayTramos && !columns.some((c) => c.appointments.length))) {
    return { columns, nadieAtiende: true, hayProfesionales: columns.length > 0 };
  }

  const startMin = Math.floor(lo / 60) * 60;
  const maxRows = 192;
  const endMin = Math.min(Math.ceil(hi / rowMinutes) * rowMinutes, startMin + maxRows * rowMinutes);
  const rows = [];
  for (let s = startMin; s < endMin; s += rowMinutes) rows.push({ startMin: s, endMin: Math.min(s + rowMinutes, endMin) });

  return {
    columns,
    rows,
    axis: { startMin, endMin },
    rowMinutes,
    nadieAtiende: false,
    hayProfesionales: columns.length > 0,
  };
}

const solapa = (a, b) => a.startMin < b.endMin && b.startMin < a.endMin;

/** Ocupación de un tramo del eje por turnos activos (con su preparación) y bloqueos (los cancelados no ocupan). */
export const ocupado = (col, segmento, axis) =>
  col.appointments.some(
    (a) => a.status !== "cancelled" && solapa({ startMin: a.startMin, endMin: a.endMin + minutosPreparacion(a) }, segmento)
  ) ||
  col.blocks.some((b) => solapa(b.allDay ? { startMin: axis.startMin, endMin: axis.endMin } : b, segmento));

/** Alguna columna tiene un turno con preparación (para mostrar la leyenda). */
export const hayPreparacion = (model) =>
  (model?.columns || []).some((c) => c.appointments.some((a) => minutosPreparacion(a) > 0));

/** Alguna columna tiene un turno que es parte de un turno fijo (para mostrar la leyenda). */
export const haySerie = (model) => (model?.columns || []).some((c) => c.appointments.some((a) => Boolean(a.series?.id)));

export const dentroDeTramo = (col, fila) =>
  col.intervals.some((w) => fila.startMin >= w.startMin && fila.startMin < w.endMin);

// Pasada la medianoche el back no agenda (los turnos empiezan antes de las 24:00), así que no se ofrece la celda.
export const filaLibre = (col, fila, axis) =>
  col.active && fila.startMin < 1440 && dentroDeTramo(col, fila) && !ocupado(col, fila, axis);

/** Mensaje de error del back o uno propio. */
export const mensajeDe = (error, fallback) => error?.response?.data?.message || fallback;
